import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { clearGameCache } from '@/api/games'
import type { PlayedEntryWithGame, Friend, CurrentUser } from '@/types'
import { GameCover } from '@/components/ui/GameCover'
import { Avatar } from '@/components/ui/Avatar'
import { StarRating } from '@/components/ui/StarRating'
import { Icon } from '@/components/ui/Icon'

interface PlayedRowProps {
  entry: PlayedEntryWithGame
  groupMembers: Friend[]
  currentUser: CurrentUser
  onRate: (rating: number) => void
  onRemove: () => void
  onDemoteToWishlist?: () => void
  onPromoteToPlaying?: () => void
  canEdit?: boolean
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

function avgRating(ratings: Record<string, number>): number | null {
  const vals = Object.values(ratings).filter((v) => v > 0)
  if (vals.length === 0) return null
  return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10
}

export function PlayedRow({
  entry,
  groupMembers,
  currentUser,
  onRate,
  onRemove,
  onDemoteToWishlist,
  onPromoteToPlaying,
  canEdit = true,
}: PlayedRowProps) {
  const { game } = entry
  const avg = avgRating(entry.ratings)
  const isAdmin = useIsAdmin()
  const queryClient = useQueryClient()

  const clearCacheMutation = useMutation({
    mutationFn: () => clearGameCache(game.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['played'] }),
  })

  return (
    <div className="cg-played-row">
      <GameCover game={game} size="sm" />

      <div className="cg-played-body">
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg-0)' }}>{game.title}</div>
        {game.tags.length > 0 && (
          <div className="cg-subtitle">{game.tags.slice(0, 3).join(' · ')}</div>
        )}
        {entry.finishedAt && (
          <div style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>
            Finished {relativeTime(entry.finishedAt)}
          </div>
        )}
        {avg !== null && (
          <div className="cg-played-avg">
            <span className="cg-played-avg-num">{avg.toFixed(1)}</span>
            <div className="cg-played-avg-label">
              group avg · {Object.values(entry.ratings).filter((v) => (v as number) > 0).length}/
              {groupMembers.length} rated
            </div>
          </div>
        )}
        {canEdit && (
          <div className="cg-played-actions" style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              className="cg-btn tiny"
              data-action="playing"
              onClick={onPromoteToPlaying}
              disabled={!onPromoteToPlaying}
            >
              <Icon name="controller" size={10} />
              Start playing
            </button>
            <button
              className="cg-btn tiny"
              data-action="wishlist"
              onClick={onDemoteToWishlist}
              disabled={!onDemoteToWishlist}
            >
              <Icon name="wishlist" size={10} />
              Back to wishlist
            </button>
          </div>
        )}
      </div>

      <div className="cg-played-ratings">
        {groupMembers.map((member) => {
          const rating = entry.ratings[member.id] ?? 0
          const isMe = member.id === currentUser.steamId
          return (
            <div key={member.id} className="cg-played-rate-row">
              <Avatar friend={member} size={18} showRing={false} />
              <span className="cg-played-rate-name">{member.nickname ?? member.name}</span>
              <StarRating
                value={rating}
                onChange={isMe ? onRate : undefined}
                readOnly={!isMe}
                size={13}
              />
            </div>
          )
        })}
        {canEdit && (
          <button
            className="cg-btn ghost tiny"
            style={{ color: 'var(--danger)', borderColor: 'oklch(from var(--danger) l c h / 0.4)', marginTop: 4 }}
            onClick={onRemove}
          >
            <Icon name="x" size={11} /> Remove
          </button>
        )}
        {isAdmin && (
          <button
            className="cg-btn ghost tiny"
            style={{ marginTop: 4 }}
            onClick={() => clearCacheMutation.mutate()}
            disabled={clearCacheMutation.isPending}
          >
            {clearCacheMutation.isPending ? 'Clearing…' : 'Clear cache'}
          </button>
        )}
      </div>
    </div>
  )
}
